import { GroupService } from './../../../../core/services/group.service';
import { UserService } from './../../../../core/services/user.service';
import { FileDownloadService } from './../../../../core/services/file-download.service';
import { SpeechRecognitionService } from 'src/app/core/services/speech-recognition.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { CALENDAR_TOOLTIPS } from '../calendar-tooltips';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { BehaviorSubject, Subscription, combineLatest, forkJoin, switchMap, take, takeUntil, debounceTime, distinctUntilChanged, EMPTY, filter, firstValueFrom, interval } from 'rxjs';
import { AppointmentTypeService } from 'src/app/core/services/appointment-type.service';
import { PatientSelectionDialogComponent } from 'src/app/shared/components/patient-selection-dialog/patient-selection-dialog.component';
import { ConsentWarningDialogComponent } from '../consent-warning-dialog/consent-warning-dialog.component';
import { OpenSlotsDialogComponent, OpenSlotsDialogResult } from '../open-slots-dialog/open-slots-dialog.component';
import { ExportDayAppointmentsDialogComponent, ExportDayAppointmentsDialogData, ExportDayAppointmentsDialogResult } from '../export-day-appointments-dialog/export-day-appointments-dialog.component';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { PUBLIC_HOLIDAYS } from './../../../../core/data/app.data';
import { effectiveDurationMinutes } from './../../../../core/utils/appointment-duration';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { MONTH_LOADING, PatientAppointmentService, TheatreListDocumentType, THEATRE_LIST_DOCUMENT_EXTENSIONS } from './../../../../core/services/patient-appointment.service';
import { PatientService } from './../../../../core/services/patient.service';
import { ScheduleService } from './../../../../core/services/schedule.service';
import { PatientConsentService } from './../../../../core/services/patient-consent.service';
import { CalendarPhysicianFilterService } from './../../../../core/services/calendar-physician-filter.service';
import { CalendarLocationFilterService } from './../../../../core/services/calendar-location-filter.service';
import { CalendarConfirmFilterService } from './../../../../core/services/calendar-confirm-filter.service';
import { canUnconfirm, confirmationTooltip, needsConfirming, statusUpdateBody } from 'src/app/core/utils/appointment-confirmation';
import { LocationService } from './../../../../core/services/location.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { applyAppointmentStatus, reoccupiesSlot, shouldWarnOfConflict } from 'src/app/core/utils/slot-capacity';
import { describeDrift, nowLine, NowLine, ScheduleDrift } from 'src/app/core/utils/schedule-drift';
import { AllDayEvent, allDayBars, combineEvents, DayPreview, dayPreview, MonthCell, monthCells, weekCells } from 'src/app/core/utils/month-grid';
import { layoutDayEvents, timeGridHours } from 'src/app/core/utils/week-grid';
import { currentFormFactor, readPreference, writePreference } from 'src/app/core/utils/device-preferences';
import { editEventExtras, entryTimeLabel, eventDayEntries, eventOccurrenceKey, isScheduleEvent } from 'src/app/core/utils/schedule-events';
import { computeOpenSlots } from 'src/app/core/utils/open-slots';
import { otherPracticeName, practiceOf } from 'src/app/core/utils/calendar-practices';
import { PracticeSwitchService } from 'src/app/core/services/practice-switch.service';
import { effectivePatientsLimit, effectivePatientsPerTimeSlot } from 'src/app/core/utils/slot-capacity';
import { movedBooking, rescheduledMessages } from 'src/app/core/utils/reschedule-messages';
import { RescheduleConfirmDialogComponent, RescheduleConfirmDialogData, RescheduleConfirmDialogResult } from '../reschedule-confirm-dialog/reschedule-confirm-dialog.component';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';

/** Rows of bars and appointment lines a month cell holds before "+n more". */
const MONTH_CELL_ROWS = 7;

export type CalendarView = 'month' | 'week' | 'day';
export const CALENDAR_VIEWS: { value: CalendarView; label: string }[] = [
  { value: 'month', label: 'Month' },
  { value: 'week', label: 'Week' },
  { value: 'day', label: 'Day' },
];
/** The view each user last chose, on phones and on desktops (`core/utils/device-preferences`). */
const CALENDAR_VIEW_PREFERENCE = 'calendar.view';
/** The doctors each user shows side by side in the day view, comma separated. */
const DAY_DOCTORS_PREFERENCE = 'calendar.dayDoctors';

/** The doctor a booking or event is for, or null for the whole practice. */
function physicianOf(row: any): string | null {
  const raw = row?.physicianId ?? row?.physician_id;
  return raw != null && String(raw).trim() !== '' ? String(raw).trim().toLowerCase() : null;
}

/** A slot in a time grid column with a place left, to book by clicking; positions are percentages of the column. */
export interface TimeGridSlot {
  time: string;
  /** Bookings already in the slot. */
  taken: number;
  /** Places left; null when the practice takes unlimited patients per slot. */
  remaining: number | null;
  top: number;
  height: number;
}

/** A booking placed in the week time grid; positions are percentages of the column. */
export interface TimeGridEvent {
  appointment: any;
  title: string;
  time: string;
  type: string;
  color: string;
  inactive: boolean;
  /** Too short for two lines: title and time share one. */
  short: boolean;
  /** The patient's file number, beside the name in the day view. */
  fileNumber: string;
  /** Second line in the day view: the reason for visit, else the diagnosis. */
  subtitle: string;
  /** The diagnosis under the reason, on a booking tall enough for a third line. */
  diagnosis: string;
  tall: boolean;
  /** Everything about the booking, one item a line, for the hover on a desktop. */
  tooltip: string;
  top: number;
  height: number;
  left: number;
  width: number;
}

export interface TimeGrid {
  hours: string[];
  /** The first minute of the day the grid shows, and how many minutes it spans. */
  startMinutes: number;
  totalMinutes: number;
  hasBars: boolean;
  /** A slot can hold more than one patient, so bookings leave a strip for the places still open. */
  sharedSlots: boolean;
  /** The time now, shown in the hours gutter level with the line in today's column. */
  nowLabel: string;
  nowTop: number | null;
  columns: TimeGridColumn[];
}

/** A day, or one doctor's bookings on the day view's day. */
export interface TimeGridColumn {
  day: any;
  /** The doctor's name when the day view shows doctors side by side. */
  label?: string;
  physicianId?: string;
  events: TimeGridEvent[];
  slots: TimeGridSlot[];
  nowTop: number | null;
}

const toMinutes = (time: string) => {
  const [h, m] = String(time).split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};
const toClock = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

export interface AppointmentDayStatus {
  key: 'default' | 'arrived' | 'busy' | 'completed' | 'cancelled' | 'did-not-attend';
  label: string;
}

/** One status per appointment for the day row. */
export function appointmentDayStatus(a: any): AppointmentDayStatus {
  if (a?.isCancelled === true) return { key: 'cancelled', label: 'Cancelled' };
  if (a?.didNotAttend === true) return { key: 'did-not-attend', label: 'Did not attend' };
  // A saved note means the visit is done, ended or not (a doctor can mark it so from the day list).
  if (a?.noteSaved === true) return { key: 'completed', label: 'Completed' };
  if (a?.endTime) {
    return a.noteSaved === false ? { key: 'busy', label: 'Note not saved' } : { key: 'completed', label: 'Completed' };
  }
  if (a?.startTime) return { key: 'busy', label: 'In progress' };
  if (a?.patientArrivedTime) return { key: 'arrived', label: 'Arrived' };
  return { key: 'default', label: '' };
}

/** The shared month's rows plus the other month's, each id once (a long event comes back in both). */
function withAdjacent(month: any[] | null, adjacent: any[]): any[] | null {
  if (!adjacent.length) return month;
  // Occurrences of a recurring event share its id, so events are told apart by their day too.
  const key = (a: any) => (a.occurrenceDate ? eventOccurrenceKey(a) : a.id);
  const ids = new Set((month ?? []).map(key));
  return [...(month ?? []), ...adjacent.filter(a => !ids.has(key(a)))];
}

/** "18 booked · 6 arrived · ..." for the patient appointments of a day; events are left out. */
export function daySummary(appointments: any[]): string {
  const patients = (appointments || []).filter(a => a?.Patient);
  const count = (key: AppointmentDayStatus['key']) => patients.filter(a => appointmentDayStatus(a).key === key).length;
  const inProgress = patients.filter(a => appointmentDayStatus(a).label === 'In progress').length;
  const notSaved = patients.filter(a => appointmentDayStatus(a).label === 'Note not saved').length;
  const parts: string[] = [
    `${patients.length - count('cancelled') - count('did-not-attend')} booked`,
    count('arrived') ? `${count('arrived')} arrived` : '',
    inProgress ? `${inProgress} in progress` : '',
    notSaved ? `${notSaved} note not saved` : '',
    count('completed') ? `${count('completed')} completed` : '',
    count('cancelled') + count('did-not-attend')
      ? `${count('cancelled') + count('did-not-attend')} cancelled or did not attend` : '',
  ];
  return parts.filter(Boolean).join(' · ');
}

/** Whether a dragged booking may tell the patient: not for a type that never sends, nor for a state unit. */
export function canNotifyOnReschedule(appointmentType: { suppressSending?: boolean } | null | undefined, group: { stateMode?: boolean } | null | undefined): boolean {
  return appointmentType?.suppressSending !== true && !isStateModeGroup(group);
}

@Component({
  selector: 'app-schedule-calendar',
  templateUrl: './schedule-calendar.component.html',
  styleUrls: ['./schedule-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Bookings show their details in the ⋮ menu too, for touch screens, where tooltips are off.
  providers: [CALENDAR_TOOLTIPS],
  animations: [
    trigger('rowAnimation', [
      // Enter transition
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'scaleY(0)',
          transformOrigin: 'top center'
        }),
        animate('0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)', style({
          opacity: 1,
          transform: 'scaleY(1)'
        })),
      ]),
      // No closing animation: switching tabs takes the open day off the page and puts the same one back, and it
      // would come back stuck at the end of it, invisible.
    ])
  ],
})
export class ScheduleCalendarComponent extends BaseComponent implements OnInit, OnDestroy {

  appointments: any[] | null = null;
  appointmentsForSelectedDay: any[] | null = null;

  currentMonth = new Date();
  readonly calendarViews = CALENDAR_VIEWS;
  calendarView: CalendarView = 'month';
  /** The user whose saved view is showing. */
  private calendarViewUserId: string | null = null;
  /** The week and day views' time grid; null in the month view. */
  timeGrid: TimeGrid | null = null;
  readonly timeGridHourHeight = 64;
  /** First day shown in the week (a Sunday) and day views. */
  viewStart = moment().startOf('day').subtract(new Date().getDay(), 'days').toDate();
  /** Doctors the day view shows side by side; none = one column for everyone. */
  dayDoctorIds: string[] = [];
  /** What the calendar last received, kept so a change of view or doctors can filter it again. */
  private loaded: { appointments: any[] | null; events: any[] | null; physicianId: string | null; locationId: string | null; unconfirmedOnly: boolean } | null = null;
  /**
   * A week can run into a second month. The shared month cache holds one month (and gets
   * the live updates); the other month's bookings and events are fetched here alongside it.
   */
  private adjacentMonth$ = new BehaviorSubject<{ appointments: any[]; events: any[] }>({ appointments: [], events: [] });
  dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  days: any[] = [];
  months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  selectedMonth: number = new Date().getMonth();
  selectedYear: number = new Date().getFullYear();
  selectedDay: Date | null = null;
  startOfWeekIndex: number | null = null;

  appointmentRowStart: number | null = null;
  appointmentColumnSpan = '1 / span 7';
  hasAppointmentsToExport = false;

  /**
   * §3b — "a list is the set of `isProcedure` appointments on a day". This is that set being
   * non-empty, and it is the ONLY thing that offers the theatre-list export. A day of
   * consultations has no theatre list to produce, so the action is not shown at all rather than
   * shown and then failing at the server.
   *
   * Recomputed in `updateAppointmentsForSelectedDay()` alongside `hasAppointmentsToExport`, from
   * the same enriched array, so the two cannot disagree about which day is on screen.
   */
  hasProcedureAppointmentsToExport = false;

  isSelectedDayFuture = false;

  availableDays: string[] = [];
  appointmentTypes: any[] = [];
  startTime: string = '00:00';
  endTime: string = '23:59';
  timeSlotDuration: string = '30'
  /** The practice's schedule settings, for how many patients a slot takes. */
  scheduleSettings: any = null;

  /** One-line summary of the selected day, shown above the compact list. */
  daySummaryText = '';
  /** Today's list only: the clock, where its line sits in the list, and how the day is running. */
  isSelectedDayToday = false;
  now = new Date();
  nowLine: NowLine | null = null;
  drift: ScheduleDrift | null = null;
  selectedFiltersCount: number = 0;
  private selectedFilterMap: Map<string, boolean> = new Map();


  isDoctor?: boolean;

  patientsWithMissingConsents: any[] = [];
  showConsentWarning: boolean = false;
  consentsChecked = false;

  private loadByMonthSubscription?: Subscription
  private patientsSubscription?: Subscription;
  private consentCheckSubscription?: Subscription;
  private isCheckingConsents: boolean = false;
  private groupId: string | null = null;

  calendarPhysicianFilterSelectValue: string = 'combined';
  doctorUsersForCalendar: { userId: string; title?: string; name?: string; surname?: string }[] = [];
  /** The group's locations; the location switcher shows only when there are some. */
  calendarLocations: any[] = [];
  /** null = all locations. */
  calendarLocationFilterId: string | null = null;

  /** Synced from `selectedGroup$` so the physician switcher and filter stay in sync with group changes. */
  calendarSplitByPhysician = false;

  /** The user's practices, to name bookings from practices other than the selected one. */
  practices: any[] = [];

  /* TODO: dynamically load this from somewhere online to ensure frequent updates */
  private publicHolidays = PUBLIC_HOLIDAYS;


  constructor(
    private userService: UserService,
    private patientAppointmentService: PatientAppointmentService,
    private patientService: PatientService,
    private scheduleService: ScheduleService,
    private appointmentTypeService: AppointmentTypeService,
    private patientConsentService: PatientConsentService,
    public groupService: GroupService,
    private calendarPhysicianFilter: CalendarPhysicianFilterService,
    private calendarLocationFilter: CalendarLocationFilterService,
    public calendarConfirmFilter: CalendarConfirmFilterService,
    private locationService: LocationService,
    private speechService: SpeechRecognitionService,
    private router: Router,
    private cd: ChangeDetectorRef,
    private snack: MatSnackBar,
    private fileDownloadService: FileDownloadService,
    private practiceSwitch: PracticeSwitchService,
    dialog: MatDialog,
    fs: FeedbackService) {
    super(fs, dialog);
  }

  ngOnInit(): void {
    // The saved view before anything is drawn, when the user is already known.
    const user = this.userService.user;
    if (user?.id) {
      this.calendarViewUserId = user.id;
      this.calendarView = this.storedCalendarView(user.id);
      this.dayDoctorIds = this.storedDayDoctors(user.id);
    }
    this.viewStart = this.periodStart(new Date());

    // A local clock tick; it only redraws the time line and the drift, nothing is fetched.
    interval(30000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isSelectedDayToday) this.refreshClock();
        // The week grid's red now line moves with the same tick.
        if (this.timeGrid) {
          this.now = new Date();
          this.generateCalendar();
          this.cd.markForCheck();
        }
      });

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe((g) => {
        this.calendarSplitByPhysician = !!g?.calendarSplitByPhysician;
        if (g?.id) this.loadAdjacentMonth();
        this.cd.markForCheck();
      });

    this.groupService.userGroups$
      .pipe(takeUntil(this.destroy$))
      .subscribe((groups: any[]) => {
        this.practices = groups || [];
        this.cd.markForCheck();
      });

    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: any[]) => {
        this.doctorUsersForCalendar = (users || []).filter(
          (u: any) => u.userType === 'doctor' && u.userId !== '511f39e1-ff14-4a6d-b35a-6fcc22923b50',
        );
        // The day view's doctor columns wait for the practice's doctors.
        if (this.calendarView === 'day' && this.dayDoctorIds.length) {
          this.applyLoaded();
          this.generateCalendar();
        }
        this.cd.markForCheck();
      });

    this.calendarPhysicianFilter.selectedPhysicianId$
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        this.calendarPhysicianFilterSelectValue = id === null ? 'combined' : id;
        this.cd.markForCheck();
      });

    this.locationService.locations$
      .pipe(takeUntil(this.destroy$))
      .subscribe((locations) => {
        this.calendarLocations = locations || [];
        this.cd.markForCheck();
      });

    this.calendarLocationFilter.selectedLocationId$
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        this.calendarLocationFilterId = id;
        this.cd.markForCheck();
      });

    combineLatest([
      this.scheduleService.schedule$,
      this.appointmentTypeService.appointmentTypes$,
      this.patientAppointmentService.monthAppointments$,
      this.scheduleService.monthEvents$,
      this.groupService.selectedGroup$,
      this.calendarPhysicianFilter.selectedPhysicianId$,
      this.calendarLocationFilter.selectedLocationId$,
      this.adjacentMonth$,
      this.calendarConfirmFilter.unconfirmedOnly$,
    ]).pipe(
      debounceTime(50),
      distinctUntilChanged((prev, curr) =>
        prev[0] === curr[0] && prev[1] === curr[1] && prev[2] === curr[2] && prev[3] === curr[3] &&
        prev[4] === curr[4] && prev[5] === curr[5] && prev[6] === curr[6] && prev[7] === curr[7] && prev[8] === curr[8]
      ),
      takeUntil(this.destroy$)
    ).subscribe(([settings, appointmentTypes, monthAppointments, monthEvents, _selectedGroup, calendarPhysicianId, calendarLocationId, adjacent, unconfirmedOnly]) => {
      const appointments = withAdjacent(monthAppointments, adjacent.appointments);
      const events = withAdjacent(monthEvents, adjacent.events);
      if (settings) {
        this.scheduleSettings = settings;
        this.availableDays = settings.availableDays;
        this.startTime = settings.startTime;
        this.endTime = settings.endTime;
        this.timeSlotDuration = settings.timeSlotDuration;
      }
      if (appointmentTypes) {
        // Initialize appointment types with selected property, preserving existing selections
        this.appointmentTypes = appointmentTypes.map((type: any) => {
          const typeKey = type.id || type.type;
          const wasSelected = this.selectedFilterMap.has(typeKey)
            ? this.selectedFilterMap.get(typeKey)
            : (type.selected !== undefined ? type.selected : false);
          return {
            ...type,
            selected: wasSelected
          };
        });
        this.updateSelectedFiltersCount();
      }
      this.loaded = { appointments, events, physicianId: calendarPhysicianId, locationId: calendarLocationId, unconfirmedOnly };
      this.applyLoaded();

      this.generateCalendar();
      this.cd.markForCheck();
    });

    this.watchUpcomingTheatreConsents();

    this.speechService.getDetectedCommand().pipe(
      takeUntil(this.destroy$)
    ).subscribe(command => {
      if (command.command === 'SHOW_APPOINTMENTS' && command.payload instanceof Date) {
        const targetDate = command.payload;

        // Move the calendar to the date if it is not on screen
        if (!this.days.some(day => day.date && moment(day.date).isSame(targetDate, 'day'))) {
          this.goToDate(targetDate);
        }

        // Find the target day within the updated calendar view
        const selectedDay = this.days.find(day => day.date &&
          day.date.getDate() === targetDate.getDate() &&
          day.date.getMonth() === targetDate.getMonth() &&
          day.date.getFullYear() === targetDate.getFullYear()
        );

        // Open the selected day
        if (selectedDay) {
          this.selectDay(selectedDay);
        }
      }
    });

    this.userService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.isDoctor = result?.userType === 'doctor';
        this.applyUserCalendarView(result);
        this.cd.markForCheck();
      });
  }

  /** Warns about upcoming theatre bookings without consent, checked again when the month or practice changes. */
  private watchUpcomingTheatreConsents(): void {
    // Check for upcoming theatre consents separately with debouncing
    // Only check when appointments are actually loaded and group is available
    combineLatest([
      this.patientAppointmentService.monthAppointments$,
      this.groupService.selectedGroup$
    ]).pipe(
      debounceTime(500), // Debounce to prevent multiple rapid calls
      // Once settled, the cleared month of a practice switch is not checked; the month that follows it is.
      filter(([appointments]) => appointments !== MONTH_LOADING),
      distinctUntilChanged((prev, curr) => {
        // Only proceed if appointments, group, or consent warning setting have changed
        const prevGroupId = prev[1]?.id;
        const currGroupId = curr[1]?.id;
        const prevAppointments = prev[0];
        const currAppointments = curr[0];
        const prevDisableWarnings = prev[1]?.disableConsentWarningsForProcedures;
        const currDisableWarnings = curr[1]?.disableConsentWarningsForProcedures;
        return prevGroupId === currGroupId &&
          prevAppointments === currAppointments &&
          prevDisableWarnings === currDisableWarnings;
      }),
      switchMap(([appointments, selectedGroup]) => {

        if (selectedGroup) {
          this.groupId = selectedGroup.id;
        }
        // If consent warnings are disabled, clear state and skip API call completely
        if (selectedGroup?.disableConsentWarningsForProcedures === true) {
          this.patientsWithMissingConsents = [];
          this.showConsentWarning = false;
          this.isCheckingConsents = false;
          this.cd.detectChanges();
          return EMPTY;
        }

        // Only check if we have a group and appointments have been loaded
        if (selectedGroup?.id &&
          appointments !== null &&
          appointments !== undefined) {
          return this.patientConsentService.checkUpcomingTheatreConsents(selectedGroup.id);
        }

        // Clear state if no group or appointments
        this.patientsWithMissingConsents = [];
        this.showConsentWarning = false;
        return EMPTY;
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: any) => {
        if (response && response.success && response.data) {
          this.patientsWithMissingConsents = response.data;
          this.showConsentWarning = this.patientsWithMissingConsents.length > 0;
        } else {
          this.patientsWithMissingConsents = [];
          this.showConsentWarning = false;
        }
        this.isCheckingConsents = false;
        this.consentsChecked = true;
        this.cd.detectChanges();
      },
      error: (error) => {
        console.error('Error checking upcoming theatre consents:', error);
        this.patientsWithMissingConsents = [];
        this.showConsentWarning = false;
        this.isCheckingConsents = false;
        this.consentsChecked = true;
      }
    });
  }

  /**
   * Filters what was loaded into the calendar's appointments. With doctors chosen on the day view,
   * their bookings and the unassigned ones show in place of the single-doctor filter.
   */
  private applyLoaded(): void {
    if (!this.loaded) return;
    const { events, physicianId, locationId, unconfirmedOnly } = this.loaded;
    if (!this.loaded.appointments && !events) return;
    // Bookings from the doctor's other practices are named, and this practice's doctor and location
    // filters leave them alone.
    const named = (this.loaded.appointments || []).map(a => ({ ...a, practiceName: otherPracticeName(a, this.groupService?.group?.id, this.practices) }));
    const appointments = named.filter(a => !a.practiceName);
    const otherPractices = named.filter(a => a.practiceName);
    const chosen = new Set(this.dayDoctorColumnIds().map(id => id.toLowerCase()));
    const byPhysician = chosen.size
      ? appointments.filter(a => { const pid = physicianOf(a); return !pid || chosen.has(pid); })
      : this.calendarPhysicianFilter.applyAppointmentFilter(appointments, this.calendarSplitByPhysician, physicianId);
    const filteredPatientAppts = [...(this.calendarLocationFilter.applyAppointmentFilter(byPhysician, locationId) ?? []), ...otherPractices];
    // "Not yet confirmed" is a call list: only bookings still to confirm, no events.
    this.appointments = this.sortAppointments(unconfirmedOnly
      ? this.calendarConfirmFilter.applyAppointmentFilter(filteredPatientAppts, true) ?? []
      : [...filteredPatientAppts, ...(events || [])]);
    this.updateSelectedFiltersCount();
    this.updateAppointmentsForSelectedDay();
  }

  /** The chosen doctors still in the practice. */
  private chosenDoctorIds(): string[] {
    return (this.dayDoctorIds ?? []).filter(id => (this.doctorUsersForCalendar ?? []).some(d => d.userId === id));
  }

  /** The day view is showing doctors side by side, in place of the single-doctor filter. */
  get showsDoctorColumns(): boolean {
    return this.dayDoctorColumnIds().length > 0;
  }

  /** The doctors given a column: the chosen ones, on the day view only. */
  private dayDoctorColumnIds(): string[] {
    return this.calendarView === 'day' ? this.chosenDoctorIds() : [];
  }

  doctorName(doctor: { title?: string; name?: string; surname?: string }): string {
    return [doctor.title, doctor.name, doctor.surname].filter(Boolean).join(' ').trim();
  }

  /** "All doctors", the one doctor's name, or how many. */
  get dayDoctorsLabel(): string {
    const ids = this.chosenDoctorIds();
    if (!ids.length) return 'All doctors';
    if (ids.length > 1) return `${ids.length} doctors`;
    return this.doctorName(this.doctorUsersForCalendar.find(d => d.userId === ids[0])!);
  }

  isDayDoctor(userId: string): boolean {
    return (this.dayDoctorIds ?? []).includes(userId);
  }

  toggleDayDoctor(userId: string): void {
    this.setDayDoctors(this.isDayDoctor(userId)
      ? this.dayDoctorIds.filter(id => id !== userId)
      : [...this.dayDoctorIds, userId]);
  }

  /** Shows these doctors side by side on the day view, and remembers them for this user. */
  setDayDoctors(ids: string[]): void {
    this.dayDoctorIds = ids;
    writePreference(localStorage, DAY_DOCTORS_PREFERENCE, this.userService?.user?.id, this.formFactor(), ids.join(','));
    this.applyLoaded();
    this.generateCalendar();
    this.cd.markForCheck();
  }

  storedDayDoctors(userId: string | null = this.userService?.user?.id): string[] {
    return (readPreference(localStorage, DAY_DOCTORS_PREFERENCE, userId, this.formFactor()) ?? '').split(',').filter(Boolean);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

    if (this.loadByMonthSubscription) {
      this.loadByMonthSubscription.unsubscribe();
    }

    if (this.patientsSubscription) {
      this.patientsSubscription.unsubscribe();
    }
  }

  shouldShowArrivalOptions(appointment: any): boolean {
    if (this.isDoctor) return false;
    return moment(appointment.date).isSame(moment(), 'day');
  }

  get calendarPhysicianFilterButtonLabel(): string {
    if (this.calendarPhysicianFilterSelectValue === 'combined') {
      return 'Combined';
    }
    const u = this.doctorUsersForCalendar.find(
      (d) => d.userId === this.calendarPhysicianFilterSelectValue,
    );
    if (!u) {
      return 'Physician';
    }
    const parts = [u.title, u.name, u.surname].filter(Boolean);
    return parts.join(' ').trim() || 'Physician';
  }

  get calendarLocationFilterButtonLabel(): string {
    return this.calendarLocations.find((l) => l.id === this.calendarLocationFilterId)?.name || 'All locations';
  }

  selectLocationFilterFromMenu(locationId: string | null): void {
    this.calendarLocationFilter.setSelectedLocationId(locationId);
  }

  selectPhysicianFilterFromMenu(physicianId: string | null): void {
    this.calendarPhysicianFilter.setSelectedPhysicianId(physicianId);
  }

  refreshClock(now: Date = new Date()): void {
    this.now = now;
    this.isSelectedDayToday = !!this.selectedDay && moment(this.selectedDay).isSame(now, 'day');
    const list = this.appointmentsForSelectedDay ?? [];
    this.nowLine = this.isSelectedDayToday ? nowLine(list, now) : null;
    this.drift = this.isSelectedDayToday ? describeDrift(list, now) : null;
    this.cd.markForCheck();
  }

  /**
   * Open slots for the next working days as a grid; a chosen slot goes into the booking flow and a
   * chosen booking opens its patient's file.
   */
  openOpenSlots(): void {
    this.dialog.open(OpenSlotsDialogComponent, {
      width: '1000px',
      maxWidth: '95vw',
      data: { startDate: this.selectedDay ? new Date(this.selectedDay) : new Date() },
    }).afterClosed().subscribe((choice?: OpenSlotsDialogResult) => {
      if (!choice) return;
      if ('timeSlot' in choice) this.openScheduleDialog(choice.timeSlot, choice.date);
      else this.navigateToPatientFile(choice.patientId);
    });
  }

  /** Time shown on a day-list row. */
  timeLabel(appointment: any): string {
    return entryTimeLabel(appointment);
  }

  buildDiagnosisString(patient: any): string {
    const { diagnosis2, diagnosis3, diagnosis4, diagnosis5 } = patient;
    const diagnoses = [diagnosis2, diagnosis3, diagnosis4, diagnosis5].filter(Boolean); // Filter out falsy values
    const diagnosisString = diagnoses.join(', ');
    return diagnosisString.length > 70 ? `${diagnosisString.substring(0, 70)}...` : diagnosisString;
  }

  private sortAppointments(appointments: any[] | null): any[] | null {
    if (!appointments?.length) {
      return appointments;
    }

    // Helper function to generate appointments for a date range
    const generateAppointmentsForRange = (event: any) => eventDayEntries(event);

    return _.chain(appointments)
      .flatMap(appointment => {
        if (appointment.date) {
          // Handle appointments with a date property
          let typeDetails;
          typeDetails = this.appointmentTypes.find((type: any) => type.id === appointment.appointmentTypeId) ?? undefined;
          if (!typeDetails) {
            typeDetails = this.appointmentTypes.find((type: any) => type.type === appointment.appointmentType) ?? undefined;
          }

          return {
            ...appointment,
            typeDetails: typeDetails ?? { type: appointment.appointmentType, backgroundColor: '#fff', textColor: '#000', borderColor: '#fff' } // Default if not found
          };
        } else if (appointment.startDate && appointment.endDate) {
          // Handle appointments with startDate and endDate properties
          return generateAppointmentsForRange(appointment);
        } else {
          return appointment;
        }
      })
      .sortBy(appointment => appointment.timeSlot)
      .value()
      .reverse();
  }

  onMonthYearChange(): void {
    this.goToDate(new Date(this.selectedYear, this.selectedMonth, 1));
  }

  /** Phone or desktop; a method so specs can choose. */
  formFactor() {
    return currentFormFactor();
  }

  /** The view this user last chose on this kind of screen; the month when none is saved. */
  storedCalendarView(userId: string | null = this.userService?.user?.id): CalendarView {
    const stored = readPreference(localStorage, CALENDAR_VIEW_PREFERENCE, userId, this.formFactor());
    // The two-week view is gone; whoever chose it gets the week.
    if (stored === 'twoWeeks') return 'week';
    return CALENDAR_VIEWS.some(v => v.value === stored) ? stored as CalendarView : 'month';
  }

  /** Shows the user's saved view once their profile is known (and again if the user changes). */
  applyUserCalendarView(user: any): void {
    const userId = user?.id ?? null;
    if (!userId || userId === this.calendarViewUserId) return;
    this.calendarViewUserId = userId;
    this.dayDoctorIds = this.storedDayDoctors(userId);
    const view = this.storedCalendarView(userId);
    if (view !== this.calendarView) {
      this.calendarView = view;
      this.goToDate(this.focusDate());
    }
  }

  /** Days the week and day views show; 0 for the month view. */
  get daysShown(): number {
    return this.calendarView === 'week' ? 7 : this.calendarView === 'day' ? 1 : 0;
  }

  get calendarViewLabel(): string {
    return CALENDAR_VIEWS.find(v => v.value === this.calendarView)?.label ?? 'Month';
  }

  /** What the arrows step by, for their tooltips. */
  get periodName(): string {
    return this.calendarView;
  }

  /** "27 Sep – 3 Oct 2026" for the week, "Wednesday 14 Oct 2026" for the day. */
  get rangeLabel(): string {
    const start = moment(this.viewStart);
    if (this.calendarView === 'day') return start.format('dddd D MMM YYYY');
    const end = start.clone().add(this.daysShown - 1, 'days');
    if (!start.isSame(end, 'year')) return `${start.format('D MMM YYYY')} – ${end.format('D MMM YYYY')}`;
    if (!start.isSame(end, 'month')) return `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
    return `${start.format('D')} – ${end.format('D MMM YYYY')}`;
  }

  /** Switches view, keeping the open day (or today, or the first day shown) on screen. */
  setCalendarView(view: CalendarView, around: Date = this.focusDate()): void {
    this.calendarView = view;
    writePreference(localStorage, CALENDAR_VIEW_PREFERENCE, this.userService?.user?.id, this.formFactor(), view);
    // The day view's doctors widen or narrow what shows.
    this.applyLoaded();
    this.goToDate(around);
  }

  private focusDate(): Date {
    if (this.selectedDay) return this.selectedDay;
    const today = new Date();
    // Nothing drawn yet: start on today.
    if (!this.days?.length) return today;
    if ((this.days ?? []).some(day => day.date && moment(day.date).isSame(today, 'day'))) return today;
    return this.daysShown ? this.viewStart : this.currentMonth;
  }

  /** The first day shown of the period holding `date`: the day itself, or the Sunday of its week. */
  private periodStart(date: Date): Date {
    const day = moment(date).startOf('day');
    return (this.calendarView === 'day' ? day : day.subtract(date.getDay(), 'days')).toDate();
  }

  /** Shows the month, week or day holding `date`, and loads its bookings. */
  goToDate(date: Date): void {
    if (this.daysShown) {
      this.viewStart = this.periodStart(date);
      const months = this.monthsInView();
      // Keep the shared month when the range still touches it, so it stays the one kept live.
      this.currentMonth = months.find(m => moment(m).isSame(this.currentMonth, 'month')) ?? months[0];
    } else {
      this.currentMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    }
    this.selectedMonth = this.currentMonth.getMonth();
    this.selectedYear = this.currentMonth.getFullYear();
    this.generateCalendar();
    this.keepSelectedDayOpen();
    this.loadAppointmentsForMonth();
  }

  /** First of each month the week view touches: one, or two when it runs over a month end. */
  private monthsInView(): Date[] {
    const start = moment(this.viewStart).startOf('month');
    const end = moment(this.viewStart).add(this.daysShown - 1, 'days').startOf('month');
    return start.isSame(end) ? [start.toDate()] : [start.toDate(), end.toDate()];
  }

  /** The open day stays open when it is still on screen; otherwise it closes. */
  private keepSelectedDayOpen(): void {
    if (!this.selectedDay) return;
    const index = this.days.findIndex(day => day.date && day.date.getTime() === this.selectedDay!.getTime());
    if (index < 0) {
      this.resetOpenRows();
      return;
    }
    this.startOfWeekIndex = index;
    this.appointmentRowStart = Math.ceil((index + 1) / 7) + 2;
  }


  trackByDate(index: number, day: any): any {
    return day.date;
  }

  trackByDay(index: number, day: any): any {
    return day.date ? day.date.getTime() : `empty-${index}`;
  }

  trackByAppointment(index: number, appointment: any): any {
    return appointment?.id ?? `slot-${index}-${appointment?.timeSlot ?? ''}`;
  }

  resetOpenRows() {
    if (!this.selectedDay) return;
    this.selectedDay = null;
    this.appointmentsForSelectedDay = null;
    this.startOfWeekIndex = null;
    this.appointmentRowStart = null;
    this.hasAppointmentsToExport = false;
    this.hasProcedureAppointmentsToExport = false;
    this.isSelectedDayFuture = false;
    this.isSelectedDayToday = false;
  }

  generateCalendar(): void {
    const cells = this.calendarView === 'day'
      ? [{ date: new Date(this.viewStart), inMonth: true }]
      : this.calendarView === 'week' ? weekCells(this.viewStart, 1) : monthCells(this.currentMonth);
    const rows = this.daysShown ? Infinity : MONTH_CELL_ROWS;
    const barEvents = this.allDayEvents(cells);
    // Events drawn as bars are left out of the counts and the lines.
    const asBars = new Set<string>(barEvents.flatMap(e => e['ids'] ?? []));
    const dateCountsMap = this.buildDateCountsMap(asBars);
    const timedByDate = this.buildTimedByDate(asBars);
    const bars = allDayBars(barEvents, cells.map(c => c.date));
    const todayDateKey = moment().format('YYYY-MM-DD');

    this.days = cells.map((cell, i) => {
      const shown = { number: cell.date.getDate(), inMonth: cell.inMonth, bars: bars[i] };
      // Days of the months either side are shown faded and cannot be opened.
      if (!cell.inMonth) {
        return { ...shown, date: null, isToday: false, isAvailable: false, appointmentCounts: [], preview: null };
      }
      const dateKey = moment(cell.date).format('YYYY-MM-DD');
      const dayName = cell.date.toLocaleDateString('en-US', { weekday: 'long' });
      return {
        ...shown,
        date: cell.date,
        appointmentCounts: dateCountsMap.get(dateKey) ?? [],
        preview: this.withTooltips(dayPreview(timedByDate.get(dateKey) ?? [], rows - bars[i].length)),
        isToday: dateKey === todayDateKey,
        isAvailable: this.availableDays.includes(dayName),
      };
    });
    this.timeGrid = this.daysShown ? this.buildTimeGrid(timedByDate) : null;
    if (this.calendarView === 'day') this.selectShownDay();
  }

  /** The day view selects its day, so the day's actions show above the grid. */
  private selectShownDay(): void {
    const day = this.days[0];
    if (!day?.isAvailable || this.selectedDay?.getTime() === day.date.getTime()) return;
    this.selectDay(day);
  }

  /**
   * Places each day's timed bookings in the time grid, overlapping ones side by side. The day view
   * with doctors chosen has a column per doctor, and one for bookings with no doctor when there are any.
   */
  private buildTimeGrid(timedByDate: Map<string, any[]>): TimeGrid {
    const slotMinutes = parseInt(this.timeSlotDuration, 10) || 30;
    const entriesOf = (day: any) => (timedByDate.get(moment(day.date).format('YYYY-MM-DD')) ?? [])
      .filter(a => a.timeSlot)
      .map(a => {
        const start = toMinutes(a.timeSlot);
        const end = a.endSlot ? toMinutes(a.endSlot) : start + effectiveDurationMinutes(a.typeDetails, { timeSlotDuration: slotMinutes });
        // A booking with no length still gets a visible block.
        return { id: String(a.id), start, end: Math.max(end, start + 15), realEnd: Math.max(end, start), appointment: a };
      });
    type Entries = ReturnType<typeof entriesOf>;
    let plan: { day: any; label?: string; physicianId?: string; entries: Entries }[] = this.days.map(day => ({ day, entries: entriesOf(day) }));
    const doctorIds = this.dayDoctorColumnIds();
    if (doctorIds.length && plan.length) {
      const { day, entries } = plan[0];
      const columnIds = doctorIds.map(id => id.toLowerCase());
      // Another practice's booking for a doctor with no column here goes with the unassigned ones.
      const unassigned = entries.filter(e => !physicianOf(e.appointment) ||
        (e.appointment.practiceName && !columnIds.includes(physicianOf(e.appointment)!)));
      plan = doctorIds.map(id => ({
        day,
        label: this.doctorName(this.doctorUsersForCalendar.find(d => d.userId === id)!),
        physicianId: id,
        entries: entries.filter(e => physicianOf(e.appointment) === id.toLowerCase()),
      }));
      if (unassigned.length) plan.push({ day, label: 'No doctor', entries: unassigned });
    }
    const perDay = plan.map(p => p.entries);
    const now = this.now ?? new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    // The week holding today stretches its hours to take in the time now, so the line always shows.
    const nowSpan = this.days.some(day => day.isToday) ? [{ start: nowMinutes, end: nowMinutes }] : [];
    const { startMinutes, endMinutes } = timeGridHours(this.startTime, this.endTime, [...perDay.flat(), ...nowSpan]);
    const total = endMinutes - startMinutes;
    const hours: string[] = [];
    for (let m = startMinutes; m < endMinutes; m += 60) hours.push(toClock(m));

    return {
      hours,
      startMinutes,
      totalMinutes: total,
      sharedSlots: effectivePatientsLimit(this.scheduleSettings) === 'unlimited' || effectivePatientsPerTimeSlot(this.scheduleSettings) > 1,
      nowLabel: toClock(nowMinutes),
      nowTop: nowSpan.length ? (nowMinutes - startMinutes) / total * 100 : null,
      hasBars: this.days.some(day => day.bars?.length),
      columns: plan.map(({ day, label, physicianId, entries }, i) => ({
        day,
        label,
        physicianId,
        slots: this.openSlots(day, entries.map(e => e.appointment), physicianId, label, startMinutes, total),
        nowTop: day.isToday && nowMinutes >= startMinutes && nowMinutes <= endMinutes
          ? (nowMinutes - startMinutes) / total * 100 : null,
        events: layoutDayEvents(perDay[i]).map(e => {
          const a = this.enrichAppointmentWithFlags(e['appointment']);
          const title = a.Patient ? [a.Patient.surname, a.Patient.name].filter(Boolean).join(', ') : (a.reason || a.appointmentType || '');
          const time = `${toClock(e.start)} – ${toClock(e['realEnd'])}`;
          return {
            appointment: a,
            title,
            time,
            fileNumber: a.Patient?.patientNumber || '',
            subtitle: a.compactDetail || a.compactDiagnosis || '',
            diagnosis: a.compactDetail ? a.compactDiagnosis || '' : '',
            tall: e.end - e.start >= 45,
            tooltip: this.bookingTooltip(a, time),
            type: a.appointmentType || '',
            color: a.typeDetails?.backgroundColor || '#009688',
            inactive: a.isCancelled === true || a.didNotAttend === true,
            short: e.end - e.start < 30,
            top: (e.start - startMinutes) / total * 100,
            height: (e.end - e.start) / total * 100,
            left: e.column / e.columns * 100,
            width: 100 / e.columns,
          };
        }),
      })),
    };
  }

  /**
   * What the ⋮ menu and the desktop hover show about a booking: what the month view's open day shows,
   * leaving out what the booking does not have.
   */
  /** The hover on a booking, in every view: its time and who, then everything `bookingDetails` lists. */
  bookingTooltip(appointment: any, time: string): string {
    const title = appointment?.Patient
      ? [appointment.Patient.surname, appointment.Patient.name].filter(Boolean).join(', ')
      : (appointment?.reason || appointment?.appointmentType || '');
    return [`${time} ${title}`, ...this.bookingDetails(appointment).map(d => `${d.label}: ${d.value}`)].join('\n');
  }

  /** A month cell's lines, each with the full hover. */
  private withTooltips(preview: DayPreview): DayPreview {
    return {
      ...preview,
      lines: preview.lines.map(l => ({ ...l, tooltip: this.enrichAppointmentWithFlags(l.appointment).tooltip })),
    };
  }

  bookingDetails(appointment: any): { label: string; value: string }[] {
    const doctor = (this.doctorUsersForCalendar ?? []).find(d => physicianOf(appointment) === String(d.userId).toLowerCase());
    return [
      { label: 'Practice', value: appointment?.practiceName },
      { label: 'File number', value: appointment?.Patient?.patientNumber },
      { label: appointment?.procedure ? 'Procedure' : 'Reason for visit', value: appointment?.compactDetail },
      { label: 'Diagnosis', value: appointment?.compactDiagnosis },
      { label: 'Type', value: appointment?.appointmentType },
      { label: 'Status', value: appointment?.dayStatus?.label },
      { label: 'Doctor', value: doctor ? this.doctorName(doctor) : '' },
      { label: 'Visit', value: appointment?.virtualLink ? 'Virtual' : '' },
      { label: 'Confirmed', value: appointment?.confirmedAt ? appointment.confirmedTooltip : '' },
    ].filter(d => !!d.value) as { label: string; value: string }[];
  }

  /**
   * The slots of a column with a place left, by the same rule as the Open Slots dialog
   * (`core/utils/open-slots`): the practice's patients per slot, busy events (those for the column's
   * doctor or the whole practice), working days only, and not once a slot is over. The "No doctor"
   * column of the day view offers none: with doctors chosen, a slot is booked with one of them.
   */
  private openSlots(day: any, bookings: any[], physicianId: string | undefined, label: string | undefined,
    gridStart: number, gridTotal: number): TimeGridSlot[] {
    if (!day?.date || !day.isAvailable || (label && !physicianId)) return [];
    const doctor = physicianId?.toLowerCase();
    const events = (this.appointments ?? []).filter(a => isScheduleEvent(a) &&
      (!label || !physicianOf(a) || physicianOf(a) === doctor));
    const schedule = { ...(this.scheduleSettings ?? {}), startTime: this.startTime, endTime: this.endTime, timeSlotDuration: this.timeSlotDuration };
    // Another practice's bookings do not fill this practice's slots.
    const own = bookings.filter(a => !isScheduleEvent(a) && !a.practiceName);
    const [openDay] = computeOpenSlots([day.date], [...own, ...events], schedule, this.now ?? new Date());
    const slotMinutes = parseInt(this.timeSlotDuration, 10) || 30;
    return openDay.cells.filter(c => c.state === 'free').map(c => ({
      time: c.time,
      taken: c.taken,
      remaining: c.remaining,
      top: (toMinutes(c.time) - gridStart) / gridTotal * 100,
      height: slotMinutes / gridTotal * 100,
    }));
  }

  /** Books a free slot of the time grid: that day, that time, and the column's doctor. */
  bookSlot(column: TimeGridColumn, slot: TimeGridSlot, event: MouseEvent): void {
    event.stopPropagation();
    this.openScheduleDialog(slot.time, column.day.date, column.physicianId);
  }

  trackBySlot(index: number, slot: TimeGridSlot): string {
    return slot.time;
  }

  /** The booking being dragged in the time grid, and where it would land. */
  draggingBooking: any = null;
  dropHint: { columnKey: any; physicianId?: string; time: string; top: number; height: number } | null = null;

  /** A patient booking of this practice that has not begun: not arrived, started, ended, cancelled or missed. */
  canDragBooking(appointment: any): boolean {
    return !!appointment?.Patient && !appointment.reason && !appointment.practiceName && appointment.isCancelled !== true &&
      appointment.didNotAttend !== true && !appointment.patientArrivedTime && !appointment.startTime && !appointment.endTime;
  }

  onTimeGridDragStart(appointment: any, event: DragEvent): void {
    if (!this.canDragBooking(appointment) || this.patientService.isRecordingLocked()) {
      event.preventDefault();
      return;
    }
    this.draggingBooking = appointment;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(appointment.id));
    }
  }

  onTimeGridDragEnd(): void {
    this.draggingBooking = null;
    this.dropHint = null;
    this.cd.markForCheck();
  }

  /**
   * The slot under the pointer, snapped to the practice's slots: on a working day, not yet begun,
   * and not where the booking already is.
   */
  private dropSlot(column: TimeGridColumn, event: DragEvent): { minutes: number; time: string } | null {
    const grid = this.timeGrid;
    const booking = this.draggingBooking;
    if (!grid || !booking || !column.day?.isAvailable) return null;
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const at = grid.startMinutes + (event.clientY - rect.top) / rect.height * grid.totalMinutes;
    const slotMinutes = parseInt(this.timeSlotDuration, 10) || 30;
    const first = toMinutes(this.startTime);
    const minutes = Math.max(grid.startMinutes, first + Math.floor((at - first) / slotMinutes) * slotMinutes);
    const time = toClock(minutes);
    if (moment(column.day.date).startOf('day').add(minutes, 'minutes').isBefore(this.now ?? new Date())) return null;
    const sameDoctor = !column.label || (physicianOf(booking) ?? undefined) === column.physicianId?.toLowerCase();
    if (moment(booking.date).isSame(column.day.date, 'day') && String(booking.timeSlot).slice(0, 5) === time && sameDoctor) return null;
    return { minutes, time };
  }

  onTimeGridDragOver(column: TimeGridColumn, event: DragEvent): void {
    const slot = this.dropSlot(column, event);
    if (!slot || !this.timeGrid) return;
    event.preventDefault(); // allows the drop
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    const slotMinutes = parseInt(this.timeSlotDuration, 10) || 30;
    this.dropHint = {
      columnKey: this.trackByColumn(0, column),
      physicianId: column.physicianId,
      time: slot.time,
      top: (slot.minutes - this.timeGrid.startMinutes) / this.timeGrid.totalMinutes * 100,
      height: slotMinutes / this.timeGrid.totalMinutes * 100,
    };
    this.cd.markForCheck();
  }

  /**
   * Asks first; on yes saves the new day and time (and the column's doctor when the day view shows
   * doctors side by side), with the patient's messages rebuilt for it, and moves the block.
   */
  onTimeGridDrop(column: TimeGridColumn, event: DragEvent): void {
    event.preventDefault();
    const appointment = this.draggingBooking;
    const slot = this.dropSlot(column, event);
    this.onTimeGridDragEnd();
    if (!appointment || !slot || this.patientService.isRecordingLocked()) return;

    const dateKey = moment(column.day.date).format('YYYY-MM-DD');
    const moved = movedBooking(appointment, dateKey, slot.time);
    const appointmentType = appointment.typeDetails?.id ? appointment.typeDetails
      : this.appointmentTypes.find((t: any) => t.id === appointment.appointmentTypeId || t.type === appointment.appointmentType);
    const data: RescheduleConfirmDialogData = {
      patientName: `${appointment.Patient.surname}, ${appointment.Patient.name}`,
      from: `${moment(appointment.date).format('ddd D MMM')} ${String(appointment.timeSlot).slice(0, 5)}`,
      to: `${moment(column.day.date).format('ddd D MMM')} ${slot.time}${column.label ? ' · ' + column.label : ''}`,
      canNotify: canNotifyOnReschedule(appointmentType, this.groupService.group),
      slotFull: !column.slots.some(s => s.time === slot.time),
    };

    this.dialog.open<RescheduleConfirmDialogComponent, RescheduleConfirmDialogData, RescheduleConfirmDialogResult>(
      RescheduleConfirmDialogComponent, { width: '420px', data })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (!result) return;
        const change: any = { ...moved };
        if (column.label) change.physicianId = column.physicianId ?? null;
        const messages = rescheduledMessages(appointment, appointmentType, {
          patientName: `${appointment.Patient.name} ${appointment.Patient.surname}`,
          groupName: this.groupService.group?.name || '',
          date: dateKey,
          timeSlot: slot.time,
        });
        this.patientAppointmentService.updateAppointment(appointment.id, { ...change, notifyPatient: result.notifyPatient, messages })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.moveLocally(appointment.id, change);
              this.showToastMessage('Appointment rescheduled');
            },
            error: (error) => {
              console.error('Error rescheduling appointment: ', error);
              this.showToastMessage('There was an error rescheduling the appointment. Please try again.');
            }
          });
      });
  }

  /** Shows a moved booking in its new place before the month's bookings are next loaded. */
  private moveLocally(id: string, change: any): void {
    const original = (this.loaded?.appointments ?? []).find(a => a.id === id);
    if (original) Object.assign(original, change);
    this.applyLoaded();
    this.generateCalendar();
    this.cd.markForCheck();
  }

  /**
   * A booking in the week grid opens the patient file, as a row of the day list does. On a phone's
   * Week view the options button is hidden to leave room for the title, so a tap opens its menu instead.
   */
  openTimeGridEvent(appointment: any, event: MouseEvent, menu?: MatMenuTrigger): void {
    if (menu && !appointment?.practiceName && this.calendarView === 'week' && window.matchMedia('(max-width: 700px)').matches) {
      event.stopPropagation();
      menu.openMenu();
      return;
    }
    if (!appointment?.Patient) return;
    event.stopPropagation();
    this.openBooking(appointment);
  }

  /** Opens a booking's patient; another practice's booking switches to that practice first. */
  async openBooking(appointment: any): Promise<void> {
    if (!appointment?.Patient) return;
    if (appointment.practiceName) {
      const practice = practiceOf(appointment, this.practices);
      if (!practice || !(await this.practiceSwitch.switchTo(practice))) return;
    }
    this.navigateToPatientFile(appointment.Patient.id);
  }

  trackByColumn(index: number, column: any): any {
    return column.physicianId ?? column.label ?? column.day?.date?.getTime?.() ?? index;
  }

  trackByTimeGridEvent(index: number, event: TimeGridEvent): any {
    return event.appointment?.id ?? index;
  }

  goToToday(): void {
    this.goToDate(new Date());
  }

  /**
   * Bars: every scheduled event (joined with matching events on the days around it) and public
   * holidays, so they stand apart from patient bookings. Timed events carry their start time.
   */
  private allDayEvents(cells: MonthCell[]): AllDayEvent[] {
    const first = moment(cells[0].date).startOf('day');
    const last = moment(cells[cells.length - 1].date).endOf('day');
    // sortAppointments copies a multi-day event onto each of its days; take each event once.
    const scheduled: AllDayEvent[] = _.uniqBy((this.appointments ?? [])
      .filter(a => !a.Patient && a.reason && a.startDate && a.endDate), eventOccurrenceKey)
      .map(a => ({
        id: eventOccurrenceKey(a),
        start: moment(a.startDate).toDate(),
        end: moment(a.endDate).toDate(),
        title: String(a.reason).trim(),
        isAllDay: !!a.isAllDay,
        startTime: a.startTime,
        endTime: a.endTime,
        time: a.isAllDay || !a.startTime || String(a.startTime).startsWith('00:00') ? '' : String(a.startTime).slice(0, 5),
        backgroundColor: a.backgroundColor,
        textColor: a.textColor,
      }));
    const events = combineEvents(scheduled);
    const holidays: AllDayEvent[] = (this.publicHolidays ?? [])
      .filter((h: any) => moment(h.date).isBetween(first, last, 'day', '[]'))
      .map((h: any) => ({
        id: `holiday-${h.date}`,
        start: moment(h.date).toDate(),
        end: moment(h.date).toDate(),
        title: h.name,
        holiday: true,
      }));
    return [...holidays, ...events];
  }

  /** The month's timed appointments by day, with the type filter applied. */
  private buildTimedByDate(asBars = new Set<string>()): Map<string, any[]> {
    const map = new Map<string, any[]>();
    const selectedTypes = this.getSelectedAppointmentTypes();
    (this.appointments ?? []).forEach(appointment => {
      if (appointment.isAllDay || asBars.has(eventOccurrenceKey(appointment))) return;
      if (selectedTypes.length > 0) {
        const appointmentType = appointment.appointmentType || appointment.typeDetails?.type || 'No Type';
        if (!selectedTypes.includes(appointmentType)) return;
      }
      const dateKey = moment(appointment.date).startOf('day').format('YYYY-MM-DD');
      if (!map.has(dateKey)) map.set(dateKey, []);
      map.get(dateKey)!.push(appointment);
    });
    return map;
  }

  private buildDateCountsMap(asBars = new Set<string>()): Map<string, any[]> {
    const map = new Map<string, { type: string; count: number; details: any }[]>();
    if (!this.appointments?.length || !this.appointmentTypes) return map;
    const selectedTypes = this.getSelectedAppointmentTypes();
    this.appointments.forEach(appointment => {
      if (appointment.isAllDay || asBars.has(eventOccurrenceKey(appointment))) return;
      if (selectedTypes.length > 0) {
        const appointmentType = appointment.appointmentType || appointment.typeDetails?.type || 'No Type';
        if (!selectedTypes.includes(appointmentType)) return;
      }
      const dateKey = moment(appointment.date).startOf('day').format('YYYY-MM-DD');
      const type = appointment.appointmentType || 'No Type';
      let arr = map.get(dateKey);
      if (!arr) {
        arr = [];
        map.set(dateKey, arr);
      }
      let entry = arr.find((e: any) => e.type === type);
      if (!entry) {
        entry = { type, count: 0, details: appointment.typeDetails || { type: appointment.appointmentType, backgroundColor: '#fff', textColor: '#000', borderColor: '#fff' } };
        arr.push(entry);
      }
      entry.count++;
    });
    return new Map(
      Array.from(map.entries()).map(([key, arr]) => [
        key,
        arr.map((e: any) => ({
          type: e.type,
          count: e.count,
          backgroundColor: e.details.backgroundColor || '#202123',
          textColor: e.details.textColor || '#202123',
          borderColor: e.details.borderColor || e.details.backgroundColor
        }))
      ])
    );
  }

  private getAppointmentCountsForDate(date: Date): any[] {
    if (!this.appointments || !this.appointmentTypes) return [];

    const targetDate = moment(date).startOf('day');
    const groupedByType: { [key: string]: { count: number, details: any } } = {};

    const selectedTypes = this.getSelectedAppointmentTypes();

    this.appointments.filter(appointment => {
      if (appointment.isAllDay) return false;
      if (selectedTypes.length > 0) {
        const appointmentType = appointment.appointmentType || appointment.typeDetails?.type || 'No Type';
        return selectedTypes.includes(appointmentType);
      }
      return true;
    }).forEach(appointment => {
      const appointmentDate = moment(appointment.date).startOf('day');
      if (appointmentDate.isSame(targetDate, 'day')) {
        const type = appointment.appointmentType || 'No Type';
        if (!groupedByType[type]) {
          groupedByType[type] = { count: 0, details: appointment.typeDetails || { type: appointment.appointmentType, backgroundColor: '#fff', textColor: '#000', borderColor: '#fff' } };
        }
        groupedByType[type].count++;
      }
    });

    return Object.keys(groupedByType).map(type => ({
      type: type,
      count: groupedByType[type].count,
      backgroundColor: groupedByType[type].details.backgroundColor || '#202123',
      textColor: groupedByType[type].details.textColor || '#202123',
      borderColor: groupedByType[type].details.borderColor || groupedByType[type].details.backgroundColor
    }));
  }


  hasAllDayAppointment(date: Date): boolean {
    if (!date) return false;

    const dateMoment = moment(date).startOf('day');
    return this.appointments?.some(app =>
      dateMoment.isBetween(moment(app.startDate).startOf('day'), moment(app.endDate).startOf('day'), 'day', '[]') && app.isAllDay
    ) || false;
  }

  getAllDayAppointment(date: Date): any {
    const eventDate = this.appointments!.find(app =>
      moment(date).isBetween(moment(app.startDate).startOf('day'), moment(app.endDate).startOf('day'), 'day', '[]') && app.isAllDay
    );
    return eventDate ? { reason: eventDate.reason, backgroundColor: eventDate.backgroundColor, textColor: eventDate.textColor } : null;
  }

  getAllDayAppointmentDetails = _.memoize((date: Date) => {
    if (!this.hasAllDayAppointment(date)) {
      return null;
    }
    return this.getAllDayAppointment(date);
  });


  hasHoliday(date: Date): boolean {
    if (!date) return false;
    const dateMoment = moment(date).startOf('day');
    return this.publicHolidays?.some(holiday =>
      dateMoment.isSame(moment(holiday.date).startOf('day'), 'day')
    ) || false;
  }

  getHoliday(date: Date): any {
    const holiday = this.publicHolidays.find(holiday =>
      moment(date).isSame(moment(holiday.date), 'day')
    );
    return holiday || null;
  }

  getPublicHolidayDetails = _.memoize((date: Date) => {
    if (!this.hasHoliday(date)) {
      return null;
    }
    return this.getHoliday(date);
  }, date => moment(date).format('YYYY-MM-DD'));


  isToday(date: Date): boolean {
    const today = new Date();
    return date?.getDate() === today.getDate() &&
      date?.getMonth() === today.getMonth() &&
      date?.getFullYear() === today.getFullYear();
  }

  isAvailable(date: Date): boolean {
    if (!date) return false;
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });

    return this.availableDays.includes(dayName); // Date is available
  }

  isFutureDate(date: Date) {

    const dateMoment = moment(date);
    // Check if date is in the past
    if (!dateMoment.isAfter(moment().subtract(1, 'days'))) {
      return false;
    }

    return true;
  }

  /** One month, week or day back (-1) or on (1). */
  navigate(step: number): void {
    if (this.daysShown) {
      this.goToDate(moment(this.viewStart).add(step * this.daysShown, 'days').toDate());
    } else {
      this.goToDate(new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + step, 1));
    }
  }


  loadAppointmentsForMonth() {
    if (this.loadByMonthSubscription) {
      this.loadByMonthSubscription.unsubscribe();
    }

    this.loadByMonthSubscription = forkJoin([
      this.patientAppointmentService.loadAppointmentsByMonth(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1),
      this.scheduleService.loadEventsByMonth(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1)]
    ).subscribe();
    this.loadAdjacentMonth();
  }

  private loadAdjacentMonthSubscription?: Subscription;

  /** The week view's second month, when it has one; cleared otherwise. */
  private loadAdjacentMonth(): void {
    this.loadAdjacentMonthSubscription?.unsubscribe();
    const other = this.daysShown
      ? this.monthsInView().find(m => !moment(m).isSame(this.currentMonth, 'month'))
      : undefined;
    if (!other) {
      const { appointments, events } = this.adjacentMonth$.value;
      if (appointments.length || events.length) this.adjacentMonth$.next({ appointments: [], events: [] });
      return;
    }
    const options = { skipSharedMonthCache: true };
    this.loadAdjacentMonthSubscription = forkJoin([
      this.patientAppointmentService.loadAppointmentsByMonth(other.getFullYear(), other.getMonth() + 1, options),
      this.scheduleService.loadEventsByMonth(other.getFullYear(), other.getMonth() + 1, options),
    ]).subscribe(([appointments, events]) =>
      this.adjacentMonth$.next({ appointments: appointments?.data ?? [], events: events?.data ?? [] }));
  }


  selectDay(selectedDay: any): void {
    if (!selectedDay.date || !selectedDay.isAvailable) return;
    const isSelected = !!this.selectedDay && this.selectedDay.getTime() === selectedDay.date.getTime();
    // The day view's one day stays selected.
    if (isSelected && this.calendarView === 'day') return;
    if (isSelected) {
      this.selectedDay = null;
      this.appointmentsForSelectedDay = null;
      this.startOfWeekIndex = null;
      this.appointmentRowStart = null;
      this.hasAppointmentsToExport = false;
      this.hasProcedureAppointmentsToExport = false;
      this.isSelectedDayFuture = false;
      this.isSelectedDayToday = false;
    } else {
      this.selectedDay = selectedDay.date;
      this.startOfWeekIndex = this.days.findIndex(day => day.date && day.date.getTime() === selectedDay.date.getTime());
      this.appointmentRowStart = this.startOfWeekIndex !== null ? Math.ceil((this.startOfWeekIndex + 1) / 7) + 2 : null;
      this.isSelectedDayFuture = moment(selectedDay.date).isAfter(moment().subtract(1, 'days'));
      this.updateAppointmentsForSelectedDay();
      return;
    }
    this.cd.markForCheck();
  }

  private updateAppointmentsForSelectedDay(): void {

    //TODO: map to appointments to determine if note exists in timeline for matching  ai request for day
    if (this.selectedDay) {
      const filteredAppointments = this.appointments?.filter(appointment => {
        const appointmentDate = moment(appointment.date);
        if (!appointmentDate.isSame(this.selectedDay, 'day')) {
          return false;
        }
        // Apply appointment type filter if any types are selected
        const selectedTypes = this.getSelectedAppointmentTypes();
        if (selectedTypes.length > 0) {
          const appointmentType = appointment.appointmentType || appointment.typeDetails?.type || 'No Type';
          return selectedTypes.includes(appointmentType);
        }
        return true;
      }) || [];

      // Calculate appointment start and end times and store them in the appointment objects
      const duration = parseInt(this.timeSlotDuration, 10);
      filteredAppointments.forEach(appointment => {
        const appointmentDate = moment(appointment.date);
        const [startHour, startMinute] = appointment.timeSlot.split(':').map(Number);

        const appointmentStart = appointmentDate.clone().set({
          hour: startHour,
          minute: startMinute,
          second: 0,
          millisecond: 0
        });

        let appointmentEnd: moment.Moment;

        if (appointment.endSlot) {
          const [endHour, endMinute] = appointment.endSlot.split(':').map(Number);
          appointmentEnd = appointmentDate.clone().set({
            hour: endHour,
            minute: endMinute,
            second: 0,
            millisecond: 0
          });
        } else {
          // Legacy row with no stored end: the type's own length if it has one, else the grid.
          appointmentEnd = appointmentStart.clone().add(
            effectiveDurationMinutes(appointment.typeDetails, { timeSlotDuration: duration }), 'minutes');
        }

        appointment.appointmentStart = appointmentStart;
        appointment.appointmentEnd = appointmentEnd;
      });

      const sortedAppointments = _.sortBy(filteredAppointments, appointment => {
        return appointment.appointmentStart.valueOf();
      });

      this.appointmentsForSelectedDay = sortedAppointments.map((a: any) => this.enrichAppointmentWithFlags(a));
      this.daySummaryText = daySummary(this.appointmentsForSelectedDay);
      this.refreshClock();
      this.hasAppointmentsToExport = (this.appointmentsForSelectedDay ?? []).some(
        (apt: any) => !apt.isAvailable && !apt.reason && apt.appointmentType !== 'Scheduled Event'
      );
      this.hasProcedureAppointmentsToExport = this.getProcedureAppointmentsForSelectedDay().length > 0;
    } else {
      this.appointmentsForSelectedDay = null;
      this.hasAppointmentsToExport = false;
      this.hasProcedureAppointmentsToExport = false;
    }
    this.cd.markForCheck();
  }





  getAppointmentRowStart(): number | null {
    return this.appointmentRowStart;
  }

  getAppointmentColumnSpan(): string {
    return this.appointmentColumnSpan;
  }

  private enrichAppointmentWithFlags(apt: any): any {
    if (!apt || apt.isAvailable) return apt;
    const dateSame = moment(apt.date).isSame(moment(), 'day');
    const dateSameOrBefore = moment(apt.date).isSameOrBefore(moment(), 'day');
    const isPast = apt.date && moment(apt.date).isBefore(moment().endOf('day'));
    const enriched = {
      ...apt,
      diagnosisSummary: apt.Patient ? this.buildDiagnosisString(apt.Patient) : '',
      // Upcoming bookings get the menu too, to confirm or cancel them before the day.
      showStatusMenu: this.isDoctor ? false : apt.appointmentType !== 'Scheduled Event' && (dateSameOrBefore || !!apt.patientId),
      showConfirmOption: needsConfirming(apt),
      showNotConfirmOption: canUnconfirm(apt),
      confirmedTooltip: confirmationTooltip(apt),
      showArrivedOption: !apt.patientArrivedTime && apt.isCancelled !== true && apt.didNotAttend !== true && dateSame,
      showNotArrivedOption: !!apt.patientArrivedTime && !apt.startTime && !apt.endTime && dateSame,
      showCancelledOption: apt.isCancelled !== true,
      showNotCancelledOption: apt.isCancelled === true,
      showDidNotAttendOption: apt.didNotAttend !== true && apt.isCancelled !== true && dateSameOrBefore,
      showDidAttendOption: apt.didNotAttend === true,
      showCompletedOption: isPast && !['completed', 'cancelled', 'did-not-attend'].includes(appointmentDayStatus(apt).key),
      showDoctorCompletionMenu: this.isDoctor === true && apt.appointmentType !== 'Scheduled Event' && isPast && apt.noteSaved !== true,
      dayStatus: appointmentDayStatus(apt),
      // Compact row: one short detail on the line, the rest on hover.
      compactDetail: apt.procedure || apt.reasonForVisit || apt.Patient?.reasonForVisiting || '',
      compactDiagnosis: [apt.Patient?.diagnosis, apt.Patient?.diagnosis2, apt.Patient?.diagnosis3, apt.Patient?.diagnosis4, apt.Patient?.diagnosis5]
        .filter(Boolean).join(' · '),
    };
    // Another practice's booking is changed from that practice, after switching to it.
    if (apt.practiceName) {
      enriched.showStatusMenu = false;
      enriched.showDoctorCompletionMenu = false;
    }
    // The same full hover as the week and day views, for the month view and the open day.
    return { ...enriched, tooltip: this.bookingTooltip(enriched, entryTimeLabel(apt)) };
  }

  addEvent() {
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    const formattedDate = moment(this.selectedDay).format('YYYY-MM-DD');
    this.router.navigate(['/patients', {
      outlets: {
        patient: ['add-event']
      }
    }], {
      queryParams: {
        date: formattedDate
      }
    });
  }

  scheduleAppointmentAt(timeSlot: string, event: MouseEvent): void {
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    event.stopPropagation();
    const formattedDate = moment(this.selectedDay).format('YYYY-MM-DD');
    this.router.navigate(['/patients', {
      outlets: {
        patient: ['add-appointment']
      }
    }], {
      queryParams: {
        date: formattedDate,
        timeSlot: timeSlot
      }
    });
  }


  /**
   * `date` defaults to the selected day; the open-slots grid passes the day it was chosen on, and a
   * doctor's column in the day view passes the doctor.
   */
  openScheduleDialog(timeSlot?: string, date?: Date, physicianId?: string): void {
    if (this.patientService.isRecordingLocked()) {
      return;
    }

    if (this.patientsSubscription) {
      this.patientsSubscription.unsubscribe();
    }

    this.patientsSubscription = this.patientService.patients$
      .pipe(
        take(1),
        switchMap(patients => {
          const dialogRef = this.dialog.open(PatientSelectionDialogComponent, {
            width: '400px',
            data: { patients }
          });
          return dialogRef.afterClosed();
        })
      )
      .subscribe(selectedPatient => {

        if (!selectedPatient) return;

        const formattedDate = moment(date ?? this.selectedDay).format('YYYY-MM-DD');
        switch (selectedPatient) {
          case 'event':
            this.router.navigate(['/patients', {
              outlets: {
                patient: ['add-event']
              }
            }], {
              queryParams: {
                date: formattedDate
              }
            });
            break;
          case 'opd':
          case 'invite':
            this.patientService.getPatientById(null)
              .pipe(takeUntil(this.destroy$))
              .subscribe(result => {
                this.router.navigate(['/patients', {
                  outlets: {
                    patient: ['add'],
                  }
                }], { queryParams: { type: selectedPatient, date: formattedDate, timeSlot: timeSlot, physicianId, redirectTo: 'add-appointment' } });
              }, error => {
                console.error(error);
                this.showToastMessage('There was an error creating the patient. Please try again.')
              });
            break;
          default:
            this.patientService.getPatientById(selectedPatient.id)
              .pipe(takeUntil(this.destroy$))
              .subscribe(result => {
                this.router.navigate(['/patients', {
                  outlets: {
                    patient: ['add-appointment']
                  }
                }], {
                  queryParams: {
                    patientId: selectedPatient.id,
                    date: formattedDate,
                    timeSlot: timeSlot,
                    physicianId
                  }
                });
              }, error => {
                console.error(error);
                this.showToastMessage('There was an error creating the patient. Please try again.')
              });

            break;
        }
      });
  }

  rescheduleAppointment(appointment: any, event: MouseEvent): void {

    event.stopPropagation();
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    //this.patientAppointmentService.setEditingAppointment(appointment);
    if (appointment.reason) {
      this.router.navigate(['/patients', {
        outlets: {
          patient: ['edit-event', appointment.id],
        }
      }], editEventExtras(appointment));
      return;
    }

    this.patientService.getPatientById(appointment.patientId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['edit-appointment', appointment.id],
          }
        }]);
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error loading the appointment for editing. Please try again.');
      });

  }

  /*   togglePatientArrived(event: MouseEvent, appointment: any, clearValue?: boolean) {
      event.stopPropagation();
      appointment.patientArrivedTime = clearValue ? null : moment().toISOString();
      const sub: Subscription = this.patientAppointmentService.updateAppointment(appointment.id, appointment).subscribe(result => sub.unsubscribe())
    } */

  // New methods for expanded appointment status options
  async updateAppointmentStatus(appointment: any, status: string, event: MouseEvent) {
    event.stopPropagation();

    const next = applyAppointmentStatus(appointment, status);
    if (reoccupiesSlot(appointment, next)) {
      const conflicts = await firstValueFrom(
        this.patientAppointmentService.findSlotConflictsFor(next, this.calendarSplitByPhysician));
      if (shouldWarnOfConflict(conflicts, this.scheduleService.currentSchedule)) {
        const proceed = await this.showConfirmationDialog('SCHEDULING CONFLICT', 'Appointments/Events already exist for this slot. Do you want to continue anyway?', conflicts);
        if (!proceed) return;
      }
    }
    Object.assign(appointment, next);
    Object.assign(appointment, this.enrichAppointmentWithFlags(appointment));

    this.patientAppointmentService.updateAppointment(appointment.id, statusUpdateBody(appointment, status)).subscribe();
  }



  // Methods to determine which menu options to show
  shouldShowArrivedOption(appointment: any): boolean {
    // Only show arrived option for today's appointments
    return !appointment.patientArrivedTime &&
      appointment.isCancelled !== true &&
      appointment.didNotAttend !== true &&
      moment(appointment.date).isSame(moment(), 'day');
  }

  shouldShowNotArrivedOption(appointment: any): boolean {
    // Only show not arrived option for today's appointments that have arrived
    return appointment.patientArrivedTime &&
      !appointment.startTime &&
      !appointment.endTime &&
      moment(appointment.date).isSame(moment(), 'day');
  }

  shouldShowCancelledOption(appointment: any): boolean {
    // Show cancelled option for all dates except when already cancelled
    return appointment.isCancelled !== true;
  }

  shouldShowNotCancelledOption(appointment: any): boolean {
    // Show not cancelled option when currently cancelled
    return appointment.isCancelled === true;
  }

  shouldShowDidNotAttendOption(appointment: any): boolean {
    // Show did not attend option for past dates and today, but not future dates
    return appointment.didNotAttend !== true &&
      appointment.isCancelled !== true &&
      moment(appointment.date).isSameOrBefore(moment(), 'day');
  }

  shouldShowDidAttendOption(appointment: any): boolean {
    // Show did attend option when currently marked as did not attend
    return appointment.didNotAttend === true;
  }

  shouldShowStatusMenu(appointment: any): boolean {
    if (this.isDoctor) return false;
    // Today and past bookings, and upcoming ones to confirm or cancel before the day.
    return appointment.appointmentType !== 'Scheduled Event' &&
      (moment(appointment.date).isSameOrBefore(moment(), 'day') || !!appointment.patientId);
  }

  // Methods to get tooltips
  getArrivedTooltip(appointment: any): string {
    return 'Mark patient as arrived';
  }

  getNotArrivedTooltip(appointment: any): string {
    return 'Mark patient as not arrived';
  }

  getCancelledTooltip(appointment: any): string {
    return 'Mark appointment as cancelled';
  }

  getNotCancelledTooltip(appointment: any): string {
    return 'Mark appointment as not cancelled';
  }

  getDidNotAttendTooltip(appointment: any): string {
    return 'Mark patient as did not attend';
  }

  getDidAttendTooltip(appointment: any): string {
    return 'Mark patient as did attend';
  }

  // Method to check if appointment is in the past
  isAppouintmentInThePast(appointment: any): boolean {
    if (!appointment.date) return false;
    const appointmentDate = moment(appointment.date);
    const today = moment().endOf('day');
    return appointmentDate.isBefore(today);
  }

  // Method to check if appointment is not completed
  isAppointmentNotCompleted(appointment: any): boolean {
    return !appointment.endTime || appointment.noteSaved === false;
  }

  // Method to mark appointment as completed
  markAppointmentAsCompleted(appointment: any, event: MouseEvent) {
    event.stopPropagation();

    if (this.patientService.isRecordingLocked()) {
      return;
    }

    // Set the appointment as completed
    appointment.endTime = moment().toISOString();
    appointment.noteSaved = true;
    appointment.isCancelled = false;
    appointment.didNotAttend = false;

    const sub: Subscription = this.patientAppointmentService.updateAppointment(appointment.id, appointment)
      .subscribe({
        next: (result) => {
          this.showToastMessage('Appointment marked as completed');
          sub.unsubscribe();
        },
        error: (error) => {
          console.error('Error updating appointment:', error);
          this.showToastMessage('There was an error updating the appointment. Please try again.');
        }
      });
  }

  // Method to check if doctor should see the completion menu
  shouldShowDoctorCompletionMenu(appointment: any): boolean {
    return this.isDoctor === true && appointment.appointmentType !== 'Scheduled Event' &&
      this.isAppouintmentInThePast(appointment) &&
      appointment.noteSaved !== true;
  }

  // Method to mark note as saved
  markNoteAsSaved(appointment: any, event: MouseEvent) {
    event.stopPropagation();

    if (this.patientService.isRecordingLocked()) {
      return;
    }

    // Completed, as saving the note from the visit does: note saved, and ended now unless it already has an end.
    appointment.noteSaved = true;
    if (!appointment.endTime) appointment.endTime = new Date().toISOString();
    Object.assign(appointment, this.enrichAppointmentWithFlags(appointment));
    this.cd.markForCheck();

    this.patientAppointmentService.updateAppointment(appointment.id, appointment)
      .subscribe({
        next: () => this.showToastMessage('Appointment marked as completed'),
        error: (error) => {
          console.error('Error updating appointment:', error);
          this.showToastMessage('There was an error updating the appointment. Please try again.');
        }
      });
  }

  navigateToPatientFile(patientId: string) {
    this.patientService.getPatientById(patientId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['file', patientId],
          }
        }]);
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error loading the patient file. Please try again.');
      });
  }

  checkUpcomingTheatreConsents(): void {
    // This method is now handled by the combineLatest subscription above
    // Keeping it for potential manual calls if needed in the future
    const selectedGroup = this.groupService.group;
    if (!selectedGroup || !selectedGroup.id || this.isCheckingConsents) {
      return;
    }

    this.isCheckingConsents = true;
    this.patientConsentService.checkUpcomingTheatreConsents(selectedGroup.id)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response: any) => {
          if (response && response.success && response.data) {
            this.patientsWithMissingConsents = response.data;
            this.showConsentWarning = this.patientsWithMissingConsents.length > 0;
          } else {
            this.patientsWithMissingConsents = [];
            this.showConsentWarning = false;
          }
          this.isCheckingConsents = false;
          this.consentsChecked = true;
          this.cd.detectChanges();
        },
        error: (error) => {
          console.error('Error checking upcoming theatre consents:', error);
          this.patientsWithMissingConsents = [];
          this.showConsentWarning = false;
          this.isCheckingConsents = false;
        }
      });
  }

  /**
   * One fixed line above the calendar unless the practice has consent warnings off. It holds its
   * place before the practice and the count arrive so the calendar never moves under the
   * person's finger.
   */
  get consentLine(): { warn: boolean; text: string } | null {
    const group = this.groupService?.group;
    if (group?.disableConsentWarningsForProcedures === true) return null;
    if (!group || !this.consentsChecked) return { warn: false, text: 'Checking consents…' };
    const count = this.patientsWithMissingConsents.length;
    if (!count) return { warn: false, text: 'All upcoming consents signed' };
    return { warn: true, text: `${count} unsigned ${count === 1 ? 'consent' : 'consents'} for upcoming procedures` };
  }

  openConsentWarningDialog(): void {
    if (this.patientsWithMissingConsents.length === 0) return;

    const dialogRef = this.dialog.open(ConsentWarningDialogComponent, {
      width: '500px',
      data: { patients: this.patientsWithMissingConsents }
    });

    dialogRef.afterClosed().subscribe((selectedPatientId: string) => {
      if (selectedPatientId) {
        this.navigateToPatientFile(selectedPatientId);
      }
    });
  }



  private openLinkInNewTab(link: string): void {
    // Try multiple methods to open the link and avoid popup blocker
    try {
      // Method 1: Create a temporary link element and trigger click
      const tempLink = document.createElement('a');
      tempLink.href = link;
      tempLink.target = '_blank';
      tempLink.rel = 'noopener noreferrer';
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
    } catch (error) {
      console.warn('Failed to open link with temporary element method:', error);
      try {
        // Method 2: Fallback to window.open
        const newWindow = window.open(link, '_blank');
        if (!newWindow) {
          console.warn('Popup blocked by browser');
          // Method 3: Show user a message to manually open the link
          this.showToastMessage('Please manually open the virtual consultation link: ' + link);
        }
      } catch (fallbackError) {
        console.error('All methods to open link failed:', fallbackError);
        this.showToastMessage('Please manually open the virtual consultation link: ' + link);
      }
    }
  }

  getSelectedAppointmentTypes(): string[] {
    if (!this.appointmentTypes) return [];
    return this.appointmentTypes
      .filter((type: any) => type.selected)
      .map((type: any) => type.type);
  }

  getSelectedFiltersCount(): number {
    const confirm = this.calendarConfirmFilter.unconfirmedOnly ? 1 : 0;
    if (!this.appointmentTypes) return confirm;
    return this.appointmentTypes.filter((type: any) => type.selected).length + confirm;
  }

  setUnconfirmedOnly(on: boolean): void {
    this.calendarConfirmFilter.setUnconfirmedOnly(on);
  }

  private updateSelectedFiltersCount(): void {
    this.selectedFiltersCount = this.getSelectedFiltersCount();
    // Update the selected filter map to preserve state
    if (this.appointmentTypes) {
      this.appointmentTypes.forEach((type: any) => {
        const typeKey = type.id || type.type;
        this.selectedFilterMap.set(typeKey, type.selected || false);
      });
    }
  }

  onFilterChange(): void {
    this.updateSelectedFiltersCount();
    this.generateCalendar();
    this.updateAppointmentsForSelectedDay();
  }

  clearFilters(): void {
    if (this.appointmentTypes) {
      this.appointmentTypes.forEach((type: any) => {
        type.selected = false;
        const typeKey = type.id || type.type;
        this.selectedFilterMap.set(typeKey, false);
      });
    }
    this.calendarConfirmFilter.setUnconfirmedOnly(false);
    this.onFilterChange();
  }

  async loadVirtualLink(appointment: any, event: MouseEvent) {
    if (this.patientService.isRecordingLocked()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    let link = appointment.virtualLink.trim();
    // Ensure the link has a protocol
    if (!link.match(/^https?:\/\//)) {
      link = 'http://' + link;
    }

    // If no patient associated with appointment, just open the link
    if (!appointment.Patient) {
      console.log('No patient associated with appointment, opening link directly');
      this.openLinkInNewTab(link);
      return;
    }

    // Ask user if they want to record
    const startRecording = await this.feedbackService.showConfirmation(
      'Start Recording?',
      'You are about to enter a virtual consultation link. Would you like to start an AI-generated recording for the consultation? Remember to return later to stop the recording once you\'re done.',
      [],
      'Go To Meeting Only',
      'Go To Meeting + Record'
    );

    if (startRecording) {
      // Navigate to patient file for recording first
      const patientId = appointment.Patient.id;

      // Clear current patient and navigate to patient file
      this.patientService.setCurrentPatient(null);
      this.router.navigate(['/patients', { outlets: { patient: null } }]);

      // Load patient and navigate to file view
      this.patientService.getPatientById(patientId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            console.log('Patient loaded successfully, navigating to file view');
            // Navigate to the patient's file view
            this.router.navigate(['/patients', {
              outlets: { patient: ['file', patientId] }
            }]);

            // Wait for navigation to complete, then trigger AI recording
            setTimeout(() => {
              console.log('Triggering AI recording');
              this.patientService.triggerAiRecording.next(true);
              this.showToastMessage('AI recording is starting. Virtual link will open in 2 seconds...');

              // Open link after a very short delay to let user see recording start
              setTimeout(() => {
                console.log('Opening virtual link in new tab');
                this.openLinkInNewTab(link);
              }, 2000); // 2 second delay - short enough to avoid popup blockers
            }, 1000);
          },
          error: error => {
            console.error('Error loading patient file:', error);
            this.showToastMessage('There was an error loading the patient file. Please try again.');
            // Still open the link even if patient file fails to load
            this.openLinkInNewTab(link);
          }
        });
    } else {
      console.log('User chose not to record, opening link directly');
      // Just open the link without recording
      this.openLinkInNewTab(link);
    }
  }

  /**
   * Returns only appointments for the selected day (excludes events and open slots).
   */
  getAppointmentsOnlyForSelectedDay(): any[] {
    if (!this.appointmentsForSelectedDay) return [];
    return this.appointmentsForSelectedDay.filter(
      (apt: any) =>
        !apt.isAvailable &&
        !apt.reason &&
        apt.appointmentType !== 'Scheduled Event'
    );
  }

  /**
   * Returns unique appointment types present in the day's appointments (appointments only, not events).
   */
  getUniqueAppointmentTypesForDay(): ExportDayAppointmentsDialogData['appointmentTypes'] {
    const appointmentsOnly = this.getAppointmentsOnlyForSelectedDay();
    const typeMap = new Map<string, { type: string; selected: boolean; backgroundColor?: string; textColor?: string; borderColor?: string }>();
    appointmentsOnly.forEach((apt: any) => {
      const typeName = apt.appointmentType || apt.typeDetails?.type || 'No Type';
      if (!typeMap.has(typeName)) {
        const details = apt.typeDetails || {};
        typeMap.set(typeName, {
          type: typeName,
          selected: true,
          backgroundColor: details.backgroundColor,
          textColor: details.textColor,
          borderColor: details.borderColor
        });
      }
    });
    return Array.from(typeMap.values());
  }

 /*  hasAppointmentsToExport(): boolean {
    return this.getAppointmentsOnlyForSelectedDay().length > 0;
  } */

  exportDayAppointments(): void {
    const appointmentsOnly = this.getAppointmentsOnlyForSelectedDay();
    if (appointmentsOnly.length === 0) {
      this.showToastMessage('No appointments to export for this day.');
      return;
    }
    const uniqueTypes = this.getUniqueAppointmentTypesForDay();
    if (uniqueTypes.length === 0) {
      this.showToastMessage('No appointments to export for this day.');
      return;
    }
    if (uniqueTypes.length === 1) {
      this.doExportDayAppointments(uniqueTypes.map(t => t.type));
      return;
    }
    const dialogRef = this.dialog.open(ExportDayAppointmentsDialogComponent, {
      width: '400px',
      data: {
        appointmentTypes: uniqueTypes,
        date: this.selectedDay
      } as ExportDayAppointmentsDialogData
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: ExportDayAppointmentsDialogResult | null) => {
      if (result && result.appointmentTypeNames.length > 0) {
        this.doExportDayAppointments(result.appointmentTypeNames);
      }
    });
  }

  // ═══ §3b — the theatre list, as a day export ═══════════════════════════════════════════
  //
  // There is no `/practice/theatre-lists` any more and no `TheatreLists` row to open. A theatre
  // list IS the day's `isProcedure` appointments, and this is where it is produced.

  /**
   * The day's procedure appointments. `typeDetails` is the `AppointmentType` row the calendar
   * already attached in `mapAppointments()`, so `isProcedure` is read from the type definition
   * rather than guessed from the type's NAME — a practice that calls its list "Theatre Slate"
   * must still work.
   */
  getProcedureAppointmentsForSelectedDay(): any[] {
    return this.getAppointmentsOnlyForSelectedDay().filter(
      (apt: any) => apt.typeDetails?.isProcedure === true
    );
  }

  /** The procedure types present on the day — what the dialog offers to filter by. */
  getProcedureAppointmentTypesForDay(): ExportDayAppointmentsDialogData['appointmentTypes'] {
    const typeMap = new Map<string, ExportDayAppointmentsDialogData['appointmentTypes'][number]>();
    this.getProcedureAppointmentsForSelectedDay().forEach((apt: any) => {
      const typeName = apt.appointmentType || apt.typeDetails?.type || 'No Type';
      if (!typeMap.has(typeName)) {
        const details = apt.typeDetails || {};
        typeMap.set(typeName, {
          type: typeName,
          selected: true,
          backgroundColor: details.backgroundColor,
          textColor: details.textColor,
          borderColor: details.borderColor
        });
      }
    });
    return Array.from(typeMap.values());
  }

  /**
   * §3b decision 2. The dialog ALWAYS opens here, even for a single procedure type: the document
   * choice is not derivable from the day, so there is nothing to skip the question for.
   */
  exportDayTheatreList(): void {
    const procedures = this.getProcedureAppointmentsForSelectedDay();
    if (procedures.length === 0) {
      this.showToastMessage('No procedures booked for this day.');
      return;
    }

    const dialogRef = this.dialog.open(ExportDayAppointmentsDialogComponent, {
      width: '420px',
      data: {
        appointmentTypes: this.getProcedureAppointmentTypesForDay(),
        date: this.selectedDay,
        title: 'Export theatre list',
        hint: 'Choose the document and which procedure types to include. The list is built from '
          + "this day's procedure appointments as they stand now.",
        // TWO documents again (2026-09-04). §3f removed the "Day Sheet" with the `.docx` day list;
        // the owner brought it back as a PDF on the practice letterhead, laid out as the
        // VeriClaim sample. The server accepts `documentType: 'daylist'` for it again.
        documentTypes: [
          { value: 'netcare', label: 'Theatre List', hint: 'The hospital booking document (Word).' },
          {
            value: 'daylist', label: 'Day Sheet',
            hint: "The team's running order for the day, as a PDF on the practice letterhead.",
          },
        ],
      } as ExportDayAppointmentsDialogData
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(async (result: ExportDayAppointmentsDialogResult | null) => {
      if (!result || !result.documentType || result.appointmentTypeNames.length === 0) return;
      if (!(await this.confirmConsentBeforeTheatreExport(result.appointmentTypeNames))) return;
      this.doExportDayTheatreList(result.documentType, result.appointmentTypeNames);
    });
  }

  /**
   * D7, in its §3b form: the consent signal moved from a theatre-list row to the moment the
   * document is produced.
   *
   * This is the last point at which anyone sees the cases together before the file leaves the
   * practice. A case reaching a hospital without a signed procedure consent is the failure this
   * feature can cheaply prevent, and the group-level banner at the top of the calendar is too far
   * away from the export to do it — it says "you have 3 unsigned consents" about the whole
   * practice, not "two of the four cases on the document you are about to send".
   *
   * WARNS, NEVER BLOCKS. There are legitimate reasons to send a list before every consent is
   * signed — consent is routinely taken on admission, and the booking has to reach the hospital
   * first. Refusing would teach people to avoid the export.
   *
   * MATCHED ON patientId, NOT appointmentId, and that is deliberate.
   * `checkUpcomingTheatreConsents` consolidates its result BY PATIENT
   * (`patientConsentController.js:709`), so a patient with two theatre appointments on one day
   * contributes ONE entry carrying only the first `appointmentId`. Matching on appointment id
   * would silently clear their second case. Over-flagging asks a clerk to re-check a consent that
   * exists; under-flagging puts a patient on a table without one.
   *
   * Returns true when the export should proceed.
   */
  private async confirmConsentBeforeTheatreExport(selectedTypes: string[]): Promise<boolean> {
    // An unanswered consent check is not a clean bill of health. If it never ran, errored, or the
    // day sits outside the endpoint's window, say nothing rather than imply everyone is consented.
    if (!Array.isArray(this.patientsWithMissingConsents) || this.patientsWithMissingConsents.length === 0) {
      return true;
    }

    const onThisDocument = this.getProcedureAppointmentsForSelectedDay()
      .filter((apt: any) => selectedTypes.includes(apt.appointmentType || apt.typeDetails?.type || ''));

    const missingIds = new Set(this.patientsWithMissingConsents.map((p: any) => p?.id).filter(Boolean));
    const affected = onThisDocument.filter((apt: any) => missingIds.has(apt.patientId));
    if (affected.length === 0) return true;

    const names = Array.from(new Set(affected.map((apt: any) => {
      const p = this.patientsWithMissingConsents.find((c: any) => c?.id === apt.patientId);
      return p ? `${p.surname ?? ''} ${p.name ?? ''}`.trim() || p.patientNumber : apt.patientId;
    })));

    return this.showConfirmationDialog(
      'UNSIGNED PROCEDURE CONSENT',
      `${affected.length} of the ${onThisDocument.length} case${onThisDocument.length === 1 ? '' : 's'} `
      + `on this document ${affected.length === 1 ? 'has' : 'have'} no signed procedure consent on file `
      + `(${names.join(', ')}). Export anyway?`,
      undefined,
      'Review consents',
      'Export anyway',
      true
    );
  }

  doExportDayTheatreList(documentType: TheatreListDocumentType, selectedTypes: string[]): void {
    if (!this.groupId) {
      this.showToastMessage('No practice selected. Please select a practice first.');
      return;
    }
    if (!this.selectedDay) return;
    const dateStr = moment(this.selectedDay).format('YYYY-MM-DD');
    // The physician filter the calendar is already showing. `null` means "combined" — every
    // surgeon's cases — which is exactly what the server should then produce.
    const physicianId = this.calendarPhysicianFilterSelectValue === 'combined'
      ? null
      : this.calendarPhysicianFilterSelectValue;

    this.patientAppointmentService
      .exportTheatreListToWord(this.groupId, dateStr, documentType, selectedTypes, physicianId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (response) => {
          // THE SERVER NAMES THE FILE (2026-09-03). This used to rebuild the name from a
          // hard-coded copy of the server's stem, which was documented as "must move together"
          // and then did not — the stem was renamed twice in three days, and the practice name
          // added on 2026-09-03 would have needed a second copy of the slug rules too.
          //
          // The fallback is deliberately DUMB: if the header is unreadable the user gets a
          // correctly-typed file with a plainer name, rather than a failed download. It does not
          // try to reproduce the server's name, because a near-copy that drifts is the thing
          // being removed here.
          const named = this.patientAppointmentService.filenameFromResponse(response);
          const blob = response.body ?? new Blob();
          // The extension DOES follow the document type even on the fallback: a PDF saved as
          // `.docx` opens in Word as garbage, which is a worse failure than a plain name.
          const extension = THEATRE_LIST_DOCUMENT_EXTENSIONS[documentType] || '.docx';
          const isDaySheet = documentType === 'daylist';
          await this.fileDownloadService.download(
            blob, named || `${isDaySheet ? 'theatre-day-sheet' : 'theatre-list'}-${dateStr}${extension}`);
          this.showToastMessage(isDaySheet ? 'Day sheet exported successfully.' : 'Theatre list exported successfully.');
        },
        error: async (err) => {
          this.showToastMessage(await this.theatreExportErrorMessage(err));
        }
      });
  }

  /**
   * The endpoint is `responseType: 'blob'`, so on a 400/404/500 Angular hands back the JSON error
   * body AS A BLOB and `err.error.message` is undefined. Reading `err.error.message` directly
   * would toast "undefined" and throw away the only useful sentence the server sent — "No
   * procedure appointments on that day for this practice." and "documentType must be one of..."
   * are both real answers a user needs to see.
   */
  private async theatreExportErrorMessage(err: any): Promise<string> {
    const fallback = 'Failed to export the theatre list.';
    const body = err?.error;

    if (body instanceof Blob) {
      try {
        const parsed = JSON.parse(await body.text());
        if (typeof parsed?.message === 'string' && parsed.message) return parsed.message;
      } catch {
        // Not JSON, or unreadable. Nothing to add — fall through to the generic message rather
        // than surfacing a parser error the user cannot act on.
      }
      return fallback;
    }

    if (typeof body?.message === 'string' && body.message) return body.message;
    return err?.message || fallback;
  }

  doExportDayAppointments(selectedTypes: string[]): void {
    if (!this.groupId) {
      this.showToastMessage('No practice selected. Please select a practice first.');
      return;
    }
    if (!this.selectedDay) return;
    const dateStr = moment(this.selectedDay).format('YYYY-MM-DD');
    this.patientAppointmentService
      .exportDayAppointmentsToExcel(this.groupId, dateStr, selectedTypes)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (blob: Blob) => {
          await this.fileDownloadService.download(blob, `appointments_${dateStr}.xlsx`);
          this.showToastMessage('Appointments exported successfully.');
        },
        error: (err) => {
          const msg = err?.error?.message || err?.message || 'Failed to export appointments.';
          this.showToastMessage(msg);
        }
      });
  }
}